/**
 * TenantContext — OS-INV-04
 *
 * Provides a lightweight tenant notion so the SINGLE Made.bb component tree
 * (Inventory, InventoryCountSheet, InventoryCountEntry, inventoryService) can be
 * reused for Offshore Sails WITHOUT forking.
 *
 * - tenant: 'madebb' | 'offshore'
 * - apiBase: derived API root. offshore -> '/api/os', madebb -> '/api/madebb'.
 * - routeBase: app route root. offshore -> '/os', madebb -> '/madebb'.
 *
 * DEFAULT is 'madebb'. Any component rendered WITHOUT a TenantProvider (i.e. all
 * existing Made.bb routes, which are not wrapped) sees tenant='madebb' and
 * apiBase='/api/madebb' — byte-for-byte unchanged Made.bb behavior.
 */

import React, { createContext, useContext, useMemo, type ReactNode } from 'react';

export type Tenant = 'madebb' | 'offshore';

export interface TenantContextValue {
  tenant: Tenant;
  /** API root for data calls, e.g. '/api/madebb' or '/api/os'. */
  apiBase: string;
  /** App route root, e.g. '/madebb' or '/os'. */
  routeBase: string;
}

const DEFAULT_TENANT: Tenant = 'madebb';

function deriveApiBase(tenant: Tenant): string {
  return tenant === 'offshore' ? '/api/os' : '/api/madebb';
}

function deriveRouteBase(tenant: Tenant): string {
  return tenant === 'offshore' ? '/os' : '/madebb';
}

// Default context value preserves existing Made.bb behavior when no provider wraps a tree.
const TenantContext = createContext<TenantContextValue>({
  tenant: DEFAULT_TENANT,
  apiBase: deriveApiBase(DEFAULT_TENANT),
  routeBase: deriveRouteBase(DEFAULT_TENANT),
});

interface TenantProviderProps {
  tenant?: Tenant;
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({
  tenant = DEFAULT_TENANT,
  children,
}) => {
  const value = useMemo<TenantContextValue>(
    () => ({
      tenant,
      apiBase: deriveApiBase(tenant),
      routeBase: deriveRouteBase(tenant),
    }),
    [tenant]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

/**
 * Read the current tenant. Defaults to 'madebb' (apiBase '/api/madebb') when no
 * TenantProvider is present, so existing Made.bb usages are unchanged.
 */
export const useTenant = (): TenantContextValue => useContext(TenantContext);
